import { useEffect, useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { useT } from "../../i18n/I18nContext";
import LanguageToggle from "./LanguageToggle";

const LINK_IDS = ["home", "services", "portfolio", "why-us", "pricing", "insights", "contact"];

export const Navbar = () => {
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const linkLabels = {
    home: t.nav.home,
    services: t.nav.services,
    portfolio: t.nav.portfolio,
    "why-us": t.nav.whyUs,
    pricing: t.nav.pricing,
    insights: t.nav.insights,
    contact: t.nav.contact,
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, id) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "uv-nav-scrolled" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <nav className="flex items-center justify-between h-20">
          <a
            href="#home"
            onClick={(e) => handleAnchor(e, "home")}
            data-testid="nav-logo"
            className="flex items-center gap-2.5 group"
          >
            <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-xl overflow-hidden bg-white ring-1 ring-slate-200">
              <img
                src="/uv-mark-64.png"
                alt="UrbanVista Web"
                width="40"
                height="40"
                className="w-full h-full object-contain"
                loading="eager"
              />
            </span>
            <span className="font-bold text-[#0A2342] tracking-tight text-lg leading-none">
              UrbanVista
              <span className="text-[#2563EB]"> Web</span>
            </span>
          </a>

          <ul className="hidden lg:flex items-center gap-7 xl:gap-9">
            {LINK_IDS.map((id) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  onClick={(e) => handleAnchor(e, id)}
                  data-testid={`nav-link-${id}`}
                  className="text-sm font-semibold text-slate-600 hover:text-[#2563EB] transition-colors"
                >
                  {linkLabels[id]}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <span className="hidden lg:inline-flex">
              <LanguageToggle />
            </span>

            <a
              href="#contact"
              onClick={(e) => handleAnchor(e, "contact")}
              data-testid="nav-cta-consultation"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#0A2342] text-white px-5 py-2.5 text-sm font-semibold shadow-sm hover:bg-[#0a2342]/90 hover:gap-3 transition-all"
            >
              {t.nav.cta}
              <ArrowRight className="w-4 h-4" />
            </a>

            <button
              type="button"
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-[#0A2342] hover:bg-slate-100 transition-colors"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </nav>

        {open && (
          <div
            data-testid="nav-mobile-panel"
            className="lg:hidden pb-6 border-t border-slate-200/70 pt-4 bg-white/95 backdrop-blur-xl rounded-b-2xl"
          >
            <ul className="flex flex-col gap-1 px-1">
              {LINK_IDS.map((id) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    onClick={(e) => handleAnchor(e, id)}
                    data-testid={`nav-mobile-link-${id}`}
                    className="block px-3 py-3 rounded-lg text-base font-semibold text-[#0A2342] hover:bg-slate-50 transition-colors"
                  >
                    {linkLabels[id]}
                  </a>
                </li>
              ))}
              <li className="pt-2 px-1">
                <LanguageToggle variant="mobile" />
              </li>
              <li className="pt-2">
                <a
                  href="#contact"
                  onClick={(e) => handleAnchor(e, "contact")}
                  data-testid="nav-mobile-cta"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0A2342] text-white px-5 py-3 text-sm font-semibold"
                >
                  {t.nav.cta}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </li>
            </ul>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
